import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { OptionsType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import * as React from 'react';
import {
  CreateCreditNoteRequest,
  CreditNoteRequestMetaData,
  CreditNotetype,
  InvoiceLineItem,
  LineItemSelect,
  MAX_MEMO_LENGTH,
  PaymentMethod,
  ReasonCodeSelect,
  RefundReasonCode,
  RefundReasons,
  FinanceReasonCodeSelect,
  FinanceRefundReasons,
  FinanceRefundReasonCode,
} from '@atlassian/cssxp-types-billing';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  ErrorComponents,
  InvoiceRefundReasonFormField,
  MultipleLineItemFormField,
  useFeatureToggle,
  InvoiceFinanceRefundReasonFormField,
} from '@css-xp/base';
import { createCreditNotesCCP } from '@atlassian/temp-proxies';
import { difference } from 'lodash';
import { ApolloQueryResult } from '@apollo/client';
import { ENABLE_SUPPORT_TICKET_AND_NOTE } from '@atlassian/cssxp-utils-generic';

interface Props {
  invoiceId: string;
  invoiceNumber: string;
  transactionAccountId: string;
  taxExempt?: boolean;
  refundType: CreditNotetype;
  isRefundLineItems: boolean;
  lineItems?: Array<InvoiceLineItem>;
  paymentMethod?: PaymentMethod;
  invoiceStatus?: string;
}

interface FormData {
  memo: string;
  financeRefundReason: FinanceReasonCodeSelect;
  lineItems?: Array<LineItemSelect>;
  internalNote: string | null;
  supportTicketNumber: string | null;
}

interface ConstructRequestDataProps {
  transactionAccountId: string;
  invoiceId: string;
  refundType: CreditNotetype;
  entitlementIds: string[] | null;
  paymentMethod: string | null | undefined;
  formData: FormData;
  invoiceStatus: string | null | undefined;
}

enum RefundHeader {
  FULL_ITEM_REFUND = 'Refund full invoice',
  LINE_ITEM_REFUND = 'Refund line items',
  TAX_ONLY_REFUND = 'Refund tax only',
}

enum WarningMessage {
  TAX_ONLY_REFUND = 'Tax Refunds are subject to evaluation of the Transaction Account being tax exempt. When the request to refund the tax amount is taken, the evaluation will happen offline. Requests determined to be ineligible will result in a full invoice refund and recharge of the refunded amount. Please confirm the customer is tax exempt before proceeding.',
  NOT_TAX_EXEMPT = 'The Ship-to party on this invoice is not tax-exempt',
}

const constructMetadata = (formData: FormData) => {
  const metadata: Record<string, string> = {};
  if (formData.internalNote) {
    metadata[CreditNoteRequestMetaData.INTERNAL_NOTE] = formData.internalNote;
  }
  if (formData.supportTicketNumber) {
    metadata[CreditNoteRequestMetaData.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
  }
  if (formData.financeRefundReason.value !== 'NONE') {
    metadata[CreditNoteRequestMetaData.FINANCE_REFUND_REASON] = formData.financeRefundReason.value;
  }
  return metadata;
};

const constructRequestData = ({
  transactionAccountId,
  invoiceId,
  refundType,
  entitlementIds,
  paymentMethod,
  formData,
  invoiceStatus,
}: ConstructRequestDataProps) => {
  const requestData: CreateCreditNoteRequest = {
    transactionAccountId: transactionAccountId,
    invoice: invoiceId,
    memo: formData.memo,
    type: refundType,
    //@ts-ignore
    reason: formData.refundReason.value.toLowerCase(),
    //@ts-ignore
    entitlementIds: entitlementIds,
    paymentMethod: paymentMethod,
    metadata: constructMetadata(formData),
    invoiceStatus: invoiceStatus,
  };
  return requestData;
};

// TO:DO waiting for invoice page.
interface InvoicePageActionProps {
  invoiceid: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export const InvoiceRefundCcpPane = ({
  invoiceId,
  invoiceNumber,
  transactionAccountId,
  refundType,
  isRefundLineItems,
  lineItems,
  taxExempt,
  paymentMethod,
  invoiceStatus,
}: Props) => {
  let options: OptionsType<LineItemSelect>;
  const { closeSidebar } = useSidebar();
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<CreateCreditNoteRequest, any>({
    submitFormAction: createCreditNotesCCP,
    onComplete: () => {
      closeSidebar();
      // Fetch the fresh data for `billingDoc`
      //refetch(); should call the fetchInvoice
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const [isEnabled] = useFeatureToggle();

  if (!transactionAccountId) {
    return (
      <InfoPanel
        type="error"
        message={
          <ErrorComponents.ErrorMessageOnInfoPanel
            title="We're unable to process refund as no transaction account found for this invoice"
            message="No trasaction account found"
          />
        }
      />
    );
  }

  const getItemsToRefund = (formData: FormData): string[] | null => {
    let items: string[] | null = null;
    if (refundType === CreditNotetype.FULL_ITEM_REFUND) {
      if (isRefundLineItems) {
        items = formData?.lineItems ? formData?.lineItems?.map((lineitem) => lineitem.value) : null;
      } else {
        items = lineItems?.map((lineitem) => lineitem.id ?? '') ?? null;
      }
    }
    return items;
  };

  const getRefundHeader = (): string => {
    switch (refundType) {
      case CreditNotetype.FULL_ITEM_REFUND:
        if (isRefundLineItems) {
          return RefundHeader.LINE_ITEM_REFUND;
        }
        return RefundHeader.FULL_ITEM_REFUND;
      case CreditNotetype.TAX_ONLY_REFUND:
        return RefundHeader.TAX_ONLY_REFUND;
      case CreditNotetype.DUNNING_CANCELLATION_CREDIT:
      case CreditNotetype.CREDIT_GRANT:
        return '';
    }
  };

  const handleFormSubmit = (formData: FormData) => {
    const lineItems = getItemsToRefund(formData);
    const requestData = constructRequestData({
      invoiceId,
      transactionAccountId,
      refundType,
      entitlementIds: lineItems,
      //@ts-ignore
      paymentMethod,
      formData,
      invoiceStatus,
    });
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('refund-processed-success')
      .withMessage('Request for refund process submitted successfully')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  if (isRefundLineItems && lineItems) {
    options = lineItems.map((lineItem) => {
      const description = lineItem?.description ? ` (${lineItem?.description})` : '';
      const label = `${lineItem?.entitlementNumber}${description}` ?? '';
      const lineItemSelect: LineItemSelect = {
        label: label,
        value: lineItem?.id ?? '',
      };
      return lineItemSelect;
    });
  }

  const mapReasonsForSelect = (reasonKeys: string[]) => {
    return reasonKeys.map(
      //@ts-ignore
      (key: RefundReasonCode) => {
        return {
          label: RefundReasons[key],
          value: key,
        };
      }
    );
  };

  const mapFinanceReasonsForSelect = (reasonKeys: string[]) => {
    return reasonKeys.map(
      //@ts-ignore
      (key: FinanceRefundReasonCode) => {
        return {
          label: FinanceRefundReasons[key],
          value: key,
        };
      }
    );
  };

  const filterReasonsForRefundType = (refundType: CreditNotetype): string[] => {
    switch (refundType) {
      case CreditNotetype.TAX_ONLY_REFUND:
        return ['REFUND_TAX'];
      default:
        return difference(Object.keys(RefundReasons), ['REFUND_TAX']);
    }
  };

  const filterFinanceReasonsForRefundType = (refundType: CreditNotetype): string[] => {
    switch (refundType) {
      case CreditNotetype.TAX_ONLY_REFUND:
        return ['REFUND_FOR_REBILL'];
      default:
        return difference(Object.keys(FinanceRefundReasons), ['REFUND_TAX']);
    }
  };

  const getRefundReasonOptions: OptionsType<ReasonCodeSelect> = mapReasonsForSelect(
    filterReasonsForRefundType(refundType)
  );

  const getFinanceRefundReasonOptions: OptionsType<FinanceReasonCodeSelect> = mapFinanceReasonsForSelect(
    filterFinanceReasonsForRefundType(refundType)
  );

  return (
    <div className="invoice-refund-ccp-form" style={{ maxWidth: '500px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={getRefundHeader()}></FormHeader>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to process refund for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {refundType === CreditNotetype.TAX_ONLY_REFUND && (
                <InfoPanel type="warning" message={WarningMessage.TAX_ONLY_REFUND} />
              )}
              <br />

              {taxExempt === false && <InfoPanel type="warning" message={WarningMessage.NOT_TAX_EXEMPT} />}
            </FormSection>
            <FormSection>
              <Field name="invoiceNumber" label="Invoice number">
                {({ fieldProps }) => (
                  <Textfield {...fieldProps} isReadOnly name="invoiceNumber" value={`${invoiceNumber}`} />
                )}
              </Field>
            </FormSection>

            <FormSection>
              <InvoiceRefundReasonFormField isRequired={true} options={getRefundReasonOptions} />
            </FormSection>

            <FormSection>
              <InvoiceFinanceRefundReasonFormField options={getFinanceRefundReasonOptions} />
            </FormSection>

            <FormSection>
              {isRefundLineItems && (
                <MultipleLineItemFormField options={options} fieldName={`lineItems`} isRequired={true} />
              )}
            </FormSection>

            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="memo"
                label="Memo"
                isRequired={true}
                validate={(value: any) => (!(value && value.length < MAX_MEMO_LENGTH) ? 'INVALID' : undefined)}
              >
                {({ fieldProps, error }) => (
                  <div>
                    <TextArea
                      {...fieldProps}
                      placeholder={`Max limit: ${MAX_MEMO_LENGTH} characters`}
                      name="memo"
                      testId={'refund-update-memo-field'}
                      isRequired={true}
                      isInvalid={error ? true : false}
                    />
                    {error && <ErrorMessage>{`Max limit: ${MAX_MEMO_LENGTH} characters`}</ErrorMessage>}
                  </div>
                )}
              </Field>
            </FormSection>

            {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && (
              <FormSection>
                <Field name="supportTicketNumber" label="Support ticket number">
                  {({ fieldProps }) => <Textfield {...fieldProps} name="supportTicketNumber" />}
                </Field>
              </FormSection>
            )}

            {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && (
              <FormSection>
                <Field<string, HTMLTextAreaElement> label="Internal Note" name="internalNote">
                  {({ fieldProps }) => <TextArea {...fieldProps} name="internalNote" />}
                </Field>
              </FormSection>
            )}

            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} testId="process-refund">
                  Process refund
                </LoadingButton>
                <Button appearance="subtle" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
